import {Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {Subject, Subscription} from "rxjs";
import {Alert, AlertController, ModalController, Platform} from "ionic-angular";
import {TranslateService} from "@ngx-translate/core";
import {Storage} from "@ionic/storage";
import {InAppBrowser} from '@ionic-native/in-app-browser';
import {LoggerProvider, LogLevel} from "../logger/logger";
import {Httpd, HttpdOptions} from '@ionic-native/httpd';
import {File} from "@ionic-native/file";
import {KeychainTouchId} from "@ionic-native/keychain-touch-id";
import {Keychain} from '@ionic-native/keychain';
import {UniqueDeviceID} from '@ionic-native/unique-device-id';
import {Deferred} from "ts-deferred";
import moment from 'moment';
import * as isNullOrEmpty from 'isnullorempty';


declare var cordova: any;


export class VersionModel {
  major: number;
  minor: number;
  revision: number;
  build: string;
  date: any;
  branch: string;
  source: string;
  channel: string;
}


@Injectable()
export class GlobalvarsProvider {
  private log_level: LogLevel = LogLevel.Error;
  private log_label_main: string = "GlobalvarsProvider";

  public apiType: string = "live";

  public version: VersionModel = new VersionModel();

  public hasKeychainTouch: boolean = false;
  private keychainKey: string = "de.s12.sonarapp";

  public hasAirplay: boolean = false;
  private airplay: any;

  public hasChromeCast: boolean = false;
  private chromecast: any;

  public playoutEvent: Subject<any> = new Subject<any>();

  private date_format_short: string;

  public appTypeEvent: Subject<any> = new Subject<any>();
  public appType: string;

  public secondPage: string;

  public storageDirectory: string = 'cdvfile://localhost/persistent/';

  public lang: string = "en";
  public langs: Array<string> = ['de','en','fr'];

  public splashTitle: string = "Lamborghini Sound by S12";
  public splashTimeout: number = 3000;

  public showChannelLabel: boolean = true;
  public logoPosition: string = "end";

  public titlePosition: string = "h1";
  public titleColor: string = "";
  public titleClass: string = "logo-black";

  public menuIcons: boolean = false;

  public frontButtonFirstColor: string = "primary";
  public frontButtonFirstBlock: boolean = true;
  public frontButtonFirstClear: boolean = false;

  public frontButtonSecondColor: string = "primary";
  public frontButtonSecondBlock: boolean = true;
  public frontButtonSecondClear: boolean = true;

  public devMode: boolean = false;               // activate or deactivate all testings

  public addPlaylistTest: boolean = false;      // add dummy playlist for clean up process

  public offsetNow: object = {};
  public offsetNowCalc: boolean = false;        // test for midnight

  public playlistChecksumTest: boolean = false;
  public trackChecksumTest: boolean = false;

  public playlistMissingTest: boolean = false;
  public trackMissingTest: boolean = false;

  public selectedChannel: any;

  public firstChannelCheck: boolean = true;

  public user: any;

  private renderer: Renderer2;

  public splashPagePromise: Promise<any>;
  private splashPageResolve: any;
  private splashPageResolved: boolean = false;

  public logConfig: boolean = false;

  public uuid: string;

  private localHttpd: Subscription;

  // 2019-03-11
  private useChromecast: boolean = false;


  constructor(
    public translateService: TranslateService,
    public storage: Storage,
    public rendererFactory: RendererFactory2,
    private iab: InAppBrowser,
    private logger: LoggerProvider,
    private alertCtrl: AlertController,
    private platform: Platform,
    private httpd: Httpd,
    private file: File,
    private keychainTouchId: KeychainTouchId,
    private keychain: Keychain,
    public modalCtrl: ModalController,
    private uniqueDeviceID: UniqueDeviceID
  ) {
    const me = this;
    let log_label: string = me.log_label_main + '::constructor';
    me.logger.setLogLevel(me, LogLevel.Warning);

    me.logger.warn(me, log_label);

    me.initSplashPagePromise();
  }

  appReady(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::appReady';

    me.logger.warn(me, log_label);

    me.logger.startTime = moment();

    // promises
    let promises: Array<any> = [];

    return new Promise((resolve, reject) => {
      // init vars
      promises.push(me.initVars());

      // splash page
      if (!me.splashPageResolved) {
        promises.push(me.splashPagePromise);
      }

      // uuid
      let deferred: Deferred<any> = new Deferred<any>();
      promises.push(deferred.promise);
      me.uniqueDeviceID.get()
        .then((uuid: any) => {
          me.logger.info(me, log_label + ':uniqueDeviceID:done', {uuid: uuid});

          me.uuid = uuid;

          me.logger.setUUID(uuid);

          deferred.resolve();

        })
        .catch((error: any) => {
          me.logger.error(me, log_label + ':uniqueDeviceID:error', {error: error});

          deferred.reject(error);
        });

      // platform
      me.logger.debug(me, log_label + ':is platform', {
        ios: me.platform.is('ios'),
        android: me.platform.is('android'),
        windows: me.platform.is('windows')
      });

      // keychain touch id
      promises.push(me.keychainTouchInit());

      // airplay
      if (me.platform.is('ios')) {
        promises.push(me.airPlayInit());
      }

      // chromecast
      if (me.platform.is('android') && me.useChromecast) {
        promises.push(me.chromecastInit());
        promises.push(me.httpdInit());
      }

      Promise.all(promises).then(() => {
        me.logger.warn(me, log_label + ':resolve');

        me.logger.setLogLevel(me, me.log_level);

        resolve();

      }, error => {
        me.logger.error(me, log_label + ':error', {error: error});

        me.logger.setLogLevel(me, me.log_level);

        reject(error);
      });
    });
  }

  private initSplashPagePromise(): void {
    const me = this;
    let log_label = me.log_label_main + '::initSplashPagePromise';

    me.logger.warn(me, log_label);

    me.splashPagePromise = new Promise(resolve => {
      me.splashPageResolve = resolve;
    });
  }

  resolveSplashPagePromise(): void {
    const me = this;
    let log_label = me.log_label_main + '::resolveSplashPagePromise';

    me.logger.warn(me, log_label);

    me.splashPageResolved = true;

    me.splashPageResolve();
  }

  afterAppReady(): void {
    const me = this;
    let log_label = me.log_label_main + '::afterAppReady';

    me.logger.warn(me, log_label);

    // date format
    me.translateService.get('date_format_short').subscribe(
      value => {
        me.logger.debug(me, log_label, {translation: value});
        me.date_format_short = value;
      }
    );
  }

  private initVars(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::initVars';

    me.logger.warn(me, log_label);

    return new Promise(resolve => {
      me.renderer = me.rendererFactory.createRenderer(null, null);

      // version
      me.version.major = 1;
      me.version.minor = 6;
      me.version.revision = 4;
      me.version.build = '799eed23a3';
      me.version.date = moment('2023-09-13T18:37:34.000Z');
      me.version.branch = 'master';
      me.version.source = 'git';
      me.version.channel = 'Production';

      me.logger.debug(me, log_label + ':dev', {
        devMode: me.devMode,
        addPlaylistTest: me.addPlaylistTest,
        offsetNowCalc: me.offsetNowCalc,
        playlistChecksumTest: me.playlistChecksumTest,
        trackChecksumTest: me.trackChecksumTest,
        playlistMissingTest: me.playlistMissingTest,
        trackMissingTest: me.trackMissingTest
      });

      if (me.offsetNowCalc && me.devMode) {
        let midnight = moment().endOf('day').subtract('1', 'minute');
        let now = moment();
        me.logger.info(me, log_label + ':midnight', {midnight: midnight, now: now});

        let minutes = midnight.diff(now, 'minutes');
        me.logger.info(me, log_label + ':minutes', {minutes: minutes});

        let hours = Math.floor(minutes / 60);
        me.offsetNow = {
          hours: hours,
          minutes: minutes - hours * 60
        };
        me.logger.info(me, log_label + ':offsetNow', {offsetNow: me.offsetNow});
      }

      me.logger.info(me, log_label + ':resolve');

      resolve();
    });
  }

  private keychainTouchInit(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::keychainTouchInit';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {lang: me.lang});

    return new Promise(resolve => {

      // 2019-09-01 check for keychain
      me.keychainTouchId.isAvailable()
        .then((result: any) => {
          me.logger.info(me, log_label + ':result', {result: result});

          me.hasKeychainTouch = true;

          resolve();
        })
        .catch((error: any) => {
          me.logger.error(me, log_label + ':error', {error: error});

          me.hasKeychainTouch = false;

          resolve();
        });
    });
  }

  saveKeychainTouch(login: string, password: string, msg: string): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::saveKeychainTouch';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {login: login, password: password, msg: msg});

    return new Promise((resolve, reject) => {
      let keychainPassword: string = JSON.stringify({login: login, password: password});
      me.logger.info(me, log_label, {keychainPassword: keychainPassword});

      me.keychainTouchId.save(me.keychainKey, keychainPassword).then(data => {
        me.logger.info(me, log_label + ":save", {data: data});

        /*
        me.keychainTouchId.verify(me.keychainKey, "Test Keychain Touch ID").then(data2 => {
          me.logger.info(me, log_label + ":verify", {data2: data2});

          resolve(data2);

        }, error => {
          me.logger.error(me, log_label + ":verify:error", {error: error});

          reject(error);
        });
        */

        resolve(data);

      }, error => {
        me.logger.error(me, log_label + ":save:error", {error: error});

        reject(error);
      })
    });
  }

  getKeychainTouch(msg: string): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getKeychainTouch';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {msg: msg});

    return new Promise((resolve, reject) => {
      me.keychainTouchId.has(me.keychainKey).then(data => {
        me.logger.info(me, log_label + ":has", {data: data, lang: me.lang});

        me.keychainTouchId.setLocale(me.lang.toLowerCase());

        me.keychainTouchId.verify(me.keychainKey, msg).then(data2 => {
          me.logger.info(me, log_label + ":verify", {data2: data2});

          resolve(JSON.parse(data2));

        }, error => {
          me.logger.error(me, log_label + ":verify:error", {error: error});

          reject(error);
        });


      }, error => {
        me.logger.error(me, log_label + ":has:error", {error: error});

        reject(error);
      });
    });
  }

  saveKeychain(login: string, password: string, msg: string): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::saveKeychain';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {login: login, password: password, msg: msg});

    return new Promise((resolve, reject) => {
      let keychainPassword: any = {login: login, password: password};
      me.logger.info(me, log_label, {keychainPassword: keychainPassword});

      me.keychain.setJson(me.keychainKey, keychainPassword, false).then(data => {
        me.logger.info(me, log_label + ":setJson", {data: data});

        /*
        me.keychain.getJson(me.keychainKey, "Test Kechain").then(data2 => {
          me.logger.info(me, log_label + ":getJson", {data2: data2});

          resolve(data2);

        }, error => {
          me.logger.error(me, log_label + ":getJson:error", {error: error});

          reject(error);
        });
        */

        resolve(data);

      }, error => {
        me.logger.error(me, log_label + ":setJson:error", {error: error});

        reject(error);
      })
    });
  }

  getKeychain(msg: string): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getKeychain';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {msg: msg});

    return new Promise((resolve, reject) => {
      me.keychain.getJson(me.keychainKey, msg).then(data2 => {
        me.logger.info(me, log_label + ":getJson", {data2: data2});

        resolve(data2);

      }, error => {
        me.logger.error(me, log_label + ":getJson:error", {error: error});

        reject(error);
      });
    });
  }

  private airPlayInit(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::airPlayInit';

    me.logger.warn(me, log_label, cordova);

    return new Promise(resolve => {
      if (cordova && cordova.plugins && cordova.plugins.airplay) {
        me.airplay = cordova.plugins.airplay;
        if (me.airplay) {
          me.hasAirplay = true;
          me.airplay.airplayInit();
        }
      }

      resolve();
    });
  }

  private chromecastInit(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::chromecastInit';

    me.logger.warn(me, log_label);

    return new Promise(resolve => {

      me.logger.debug(me, log_label + ":info", {
        cordova: cordova,
        plugins: cordova.plugins
      });

      let initChromeCast: any = function (receiverId: number) {
        let sessionListener: any = function (e) {
          me.logger.warn(me, log_label, ":sessionListener", e);
        };

        let receiverListener: any = function (e) {
          me.logger.warn(me, log_label, ":receiverListener", e);

          if (e === cordova.plugins.chromecast.ReceiverAvailability.AVAILABLE) {
            // found
            me.hasChromeCast = true;

          } else {
            // retry
            /**/
            setTimeout(() => {
              me.chromecastInit();
            }, 30000);
            /**/

          }
        };

        let sessionRequest: any = new cordova.plugins.chromecast.SessionRequest(receiverId);
        let apiConfig: any = new cordova.plugins.chromecast.ApiConfig(sessionRequest, sessionListener, receiverListener);

        let onInitSuccess: any = function (e) {
          me.logger.warn(me, log_label, ":onInitSuccess", e);

          resolve();
        };

        let onError: any = function (e) {
          me.logger.warn(me, log_label, ":onError", e);
        };

        me.logger.info(me, log_label, {
          sessionRequest: sessionRequest,
          apiConfig: apiConfig
        });

        cordova.plugins.chromecast.initialize(apiConfig, onInitSuccess, onError);
      };

      /*
      let waitFor = function(condition, callback) {
        if (!condition) {
          initChromeCast();

          me.logger.warn(me, log_label, {condition: condition, status: "wait"});
          window.setTimeout(waitFor.bind(null, condition, callback), 1000);

        } else {
          me.logger.warn(me, log_label, {condition: condition, status: "waited"});
          callback();
        }
      };
      */

      //waitFor(me.hasChromeCast, resolve());
      initChromeCast(cordova.plugins.chromecast.media.DEFAULT_MEDIA_RECEIVER_APP_ID);

      // resolve
      //resolve();
    });
  }

  httpdStop(): void {
    const me = this;
    let log_label = me.log_label_main + '::httpdStop';

    me.logger.warn(me, log_label);

    if (me.localHttpd) {
      me.localHttpd.unsubscribe();
    }
  }

  private httpdInit(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::httpdInit';

    me.logger.warn(me, log_label);

    return new Promise(resolve => {

      me.file.resolveLocalFilesystemUrl(me.storageDirectory).then(entry => {
        me.logger.warn(me, log_label + ":dir", {entry: entry});

        let www_root: string = '/storage/emulated/0/Android/data/de.s12.sonarapp/files/'; // entry.nativeURL.replace("file://", "");

        let options: HttpdOptions = {
          www_root: www_root, // relative path to app's www directory
          port: 8012,
          localhost_only: false
        };

        me.localHttpd = me.httpd.startServer(options).subscribe((data) => {
          me.logger.warn(me, log_label + ":ready", {data: data});

          me.httpd.getUrl().then((data) => {
            me.logger.warn(me, log_label + ":ready:url", {data: data});
          });

          me.httpd.getLocalPath().then((data) => {
            me.logger.warn(me, log_label + ":ready:local", {data: data});
          });

          resolve();
        });

      }, error => {
        me.logger.error(me, log_label + ":dir:error", {error: error});
      });
    });
  }

  getVersion(): string {
    return "v" + this.version.major + "." + this.version.minor + "." + this.version.revision; // + " (#" + this.version.build +" " + this.version.date.format(this.date_format_short) + " / " + this.version.branch + ")";
  }

  isLoggedIn(): boolean {
    const me = this;
    let log_label = me.log_label_main + '::isLoggedIn';

    //me.logger.warn(me, log_label);
    me.logger.debug(me, log_label, {user: me.user, userCheck: isNullOrEmpty.isNullOrEmpty(me.user)});

    if (isNullOrEmpty.isNullOrEmpty(me.user)) {
      return false;

    } else {
      return true;
    }
  }

  openExternalLink(link: string): void {
    const me = this;
    let log_label = me.log_label_main + '::openExternalLink';

    me.logger.warn(me, log_label);
    me.logger.debug(me, log_label, {link: link, lang: this.lang});

    let url: string = "";
    switch (link) {
      case "imprint":
        url = "https://www.s12.de/en/imprint/";
        if (this.lang == "de") {
          url = "https://www.s12.de/de/impressum/";
        }
        break;

      case "privacy":
        url = "https://www.s12.de/en/privacy/";
        if (this.lang == "de") {
          url = "https://www.s12.de/de/datenschutz/";
        }
        break;

      case "version":
        me.showVersion();
        return;
    }
    me.logger.warn(me, log_label, {url: url});

    if (url.length > 0) {
      // 2018-10-30
      //window.open(url, "_blank");
      //window.open(url, "_system");
      // 2018-12-11
      //me.iab.create(url, "_system");
      //me.iab.create(url, "_self", "location=no,footer=yes");
      // 2019-01-02
      if (me.platform.is('core')) {
        // desktop browser
        window.open(url, "_blank");

      } else {
        // mobile device
        me.iab.create(url, "_blank", "location=no,footer=yes");
      }
    }
  }

  setSelectedChannel(channel: any): void {
    const me = this;
    let log_label = me.log_label_main + '::setSelectedChannel';

    me.logger.warn(me, log_label);
    me.logger.debug(me, log_label, {channel: channel});

    me.selectedChannel = channel;

    me.storage.set('last_channel', channel).then(() => {
      me.logger.debug(me, log_label + ':set_last_channel:done', {result: channel});

    }, error => {
      me.logger.error(me, log_label + ':set_last_channel:error', {error: error});
    });
  }

  loadSelectedChannel(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::loadSelectedChannel';

    me.logger.warn(me, log_label);

    return new Promise((resolve, reject) => {
      me.storage.get('last_channel').then((channel) => {
        me.logger.debug(me, log_label + ':get_last_channel:done', {result: channel});

        if (isNullOrEmpty.isNullOrEmpty(channel)) {
          reject("empty channel from storage");

        } else {

          // TODO: maybe redundant
          me.selectedChannel = channel;

          resolve(channel);
        }

      }, error => {
        me.logger.error(me, log_label + ':get_last_channel:error', {error: error});
        reject(error);
      });
    })
  }

  setUser(user: any): void {
    const me = this;
    let log_label = me.log_label_main + '::setUser';

    me.logger.warn(me, log_label);
    me.logger.debug(me, log_label, {user: user});

    me.user = user;

    // 2019-02-25
    if (!me.splashPageResolved) {
      me.resolveSplashPagePromise();
    }

    // 2019-01-03, set theme for mobile apps only
    me.logger.debug(me, log_label, {
      android: me.platform.is('android'),
      ios: me.platform.is('ios'),
      core: me.platform.is('core')
    });

    if (
      (
        me.platform.is('android')
        ||
        me.platform.is('ios')
      )
      &&
      !me.platform.is('core')
    ) {
      me.setTheme();
    }
  }

  resetTheme(): void {
    const me = this;
    let log_label = me.log_label_main + '::resetTheme';

    me.logger.warn(me, log_label);

    me.user.app_theme = "s12";

    me.setTheme();
  }

  setTheme(): void {
    const me = this;
    let log_label = me.log_label_main + '::setTheme';

    me.logger.warn(me, log_label);
    me.logger.debug(me, log_label, {user: me.user, document: document.documentElement});

    if (!isNullOrEmpty.isNullOrEmpty(me.user.app_theme)) {
      switch (me.user.app_theme) {
        case "porsche":
          me.renderer.removeClass(document.documentElement, 'audi-theme');
          me.renderer.removeClass(document.documentElement, 's12-theme');
          me.renderer.addClass(document.documentElement, 'porsche-theme');

          me.frontButtonFirstColor = "primary";
          me.frontButtonFirstBlock = true;
          me.frontButtonFirstClear = false;

          me.frontButtonSecondColor = "primary";
          me.frontButtonSecondBlock = true;
          me.frontButtonSecondClear = true;

          me.titlePosition = "h1";
          me.titleColor = "";
          me.titleClass = "logo-black";

          me.menuIcons = false;

          break;

        // 2019-03-26
        case "audi":
          me.renderer.removeClass(document.documentElement, 's12-theme');
          me.renderer.removeClass(document.documentElement, 'porsche-theme');
          me.renderer.addClass(document.documentElement, 'audi-theme');

          me.frontButtonFirstColor = "primary";
          me.frontButtonFirstBlock = true;
          me.frontButtonFirstClear = false;

          me.frontButtonSecondColor = "primary";
          me.frontButtonSecondBlock = true;
          me.frontButtonSecondClear = true;

          me.titlePosition = "h1";
          me.titleColor = "";
          me.titleClass = "logo-black";

          me.menuIcons = false;

          break;

        case "s12":
        default:
          me.renderer.removeClass(document.documentElement, 'audi-theme');
          me.renderer.removeClass(document.documentElement, 'porsche-theme');
          me.renderer.addClass(document.documentElement, 's12-theme');

          me.frontButtonFirstColor = "primary";
          me.frontButtonFirstBlock = true;
          me.frontButtonFirstClear = false;

          me.frontButtonSecondColor = "primary";
          me.frontButtonSecondBlock = true;
          me.frontButtonSecondClear = false;

          me.titlePosition = "ion-title-center";
          me.titleColor = "dark";
          me.titleClass = "";

          me.menuIcons = true;
      }
    }
  }

  showVersion(): void {
    const me = this;
    let log_label = me.log_label_main + '::showVersion';

    me.logger.warn(me, log_label);

    me.translateService.get(['version_title',
      'version_label_versionnumber',
      'version_label_buildnumber',
      'version_label_builddate',
      'version_label_vcs',
      'version_label_branch',
      'version_label_channel',
      'version_label_api',
      'version_label_dev',
      'version_label_logconfig',
      'buttons_ok',
      'buttons_debugging'
    ]).subscribe(value => {
      me.logger.debug(me, log_label, {translation: value});

      let subTitle: string = "";
      subTitle += value.version_label_versionnumber + ': ' + me.getVersion();
      subTitle += "<br />";
      subTitle += value.version_label_buildnumber + ': #' + me.version.build;
      subTitle += "<br />";
      subTitle += value.version_label_builddate + ': ' + me.version.date.format(me.date_format_short);
      subTitle += "<br />";
      subTitle += "<br />";
      subTitle += value.version_label_vcs + ': ' + me.version.source;
      subTitle += "<br />";
      subTitle += value.version_label_branch + ': ' + me.version.branch;
      subTitle += "<br />";
      subTitle += "<br />";
      subTitle += value.version_label_channel + ': ' + me.version.channel;
      subTitle += "<br />";
      subTitle += "<br />";
      subTitle += value.version_label_api + ': ' + me.apiType;
      subTitle += "<br />";
      subTitle += "<br />";
      subTitle += value.version_label_logconfig + ': ' + me.logConfig;
      subTitle += "<br />";
      subTitle += "<br />";
      subTitle += value.version_label_dev + ': ' + me.devMode;

      let alert = me.alertCtrl.create({
        title: value.version_title,
        subTitle: subTitle,
        buttons: [
          {
            text: value.buttons_debugging,
            handler: () => {
              me.logConfig = !me.logConfig;
            }
          },
          value.buttons_ok
        ]
      });

      alert.present();
    });
  }

  openAirPlay(): void {
    const me = this;
    let log_label = me.log_label_main + '::openAirPlay';

    me.logger.warn(me, log_label);

    me.airplay.airplayButtonPress();
  }

  openChromeCast(): void {
    const me = this;
    let log_label = me.log_label_main + '::openChromeCast';

    me.logger.warn(me, log_label, {cast: cordova.plugins.chromecast, session: me.chromecast});

    if (me.chromecast) {
      me.chromecast.leave();

      me.chromecast = null;

      me.playoutEvent.next("self");
    }

    let onRequestSessionSuccess: any = function (e) {
      me.logger.warn(me, log_label, ":onRequestSessionSuccess", e);

      me.chromecast = e;

      me.playoutEvent.next("chromecastSession");
    };

    let onLaunchError: any = function (e) {
      me.logger.error(me, log_label, ":onLaunchError", e);
    };

    //cordova.plugins.chromecast.getRouteListElement(onRequestSessionSuccess, onLaunchError);
    //cordova.plugins.chromecast.requestSession(onRequestSessionSuccess, onLaunchError);
    let routes: any = cordova.plugins.chromecast.getRouteRaw();
    me.logger.warn(me, log_label, ":routes", {routes: routes});

    // translatation
    me.translateService.get(['sync_service_chromecast_title', 'sync_service_chromecast_ok', 'sync_service_chromecast_cancel']).subscribe(
      value => {
        me.logger.debug(me, log_label, {translation: value});

        let alert: Alert = me.alertCtrl.create();
        alert.setTitle(value.sync_service_chromecast_title);

        Object.keys(routes).forEach(key => {
          alert.addInput({
            type: 'radio',
            label: routes[key].name,
            value: key
          });
        });

        alert.addButton(value.sync_service_chromecast_cancel);

        alert.addButton({
          text: value.sync_service_chromecast_ok,
          handler: (data: any) => {
            me.logger.warn(me, log_label, ":selected", {data: data});

            cordova.plugins.chromecast.selectRouteRaw(data, onRequestSessionSuccess, onLaunchError);
          }
        });

        alert.present();
      }
    );
  }

  getChromeCast(): any {
    const me = this;
    let log_label = me.log_label_main + '::getChromeCast';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {cast: cordova.plugins.chromecast, session: me.chromecast});

    return me.chromecast;
  }

  openLogConfig(view: any): void {
    const me = this;
    let log_label = me.log_label_main + '::openLogConfig';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {view: view});

    const modal = this.modalCtrl.create('LogConfigPage', {view: view});
    modal.present();
  }
}




// WEBPACK FOOTER //
// ./src/providers/globalvars/globalvars.ts